import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FiArrowRight } from "react-icons/fi";
import orzelLogo from "../../img/orzel_logo_192.png";

// Dymek prowadzacy samouczka.
//
// Wczesniej byl to staly panel przyklejony do dolu ekranu — zaslanial FAB
// brudnopisu, stopke ze zrodlem i przycisk "kolejne zadanie", czyli dokladnie te
// elementy, o ktorych opowiadal. Teraz kotwiczy sie przy omawianym elemencie:
// mierzy jego prostokat i staje NAD nim albo POD nim, po tej stronie gdzie jest
// miejsce, z dziobkiem wskazujacym cel. Gdy krok nie ma celu (intro) — wraca na
// dol ekranu.
// Jedno podswietlenie dla wszystkich elementow samouczka: zlota obwodka + tetniaca
// aureola. Statyczny `ring-*` jest fallbackiem dla prefers-reduced-motion — przy
// wlaczonej animacji ramke rysuja same klatki (patrz tailwind.config.js).
export const TUTORIAL_HIGHLIGHT =
    "ring-[3px] ring-[#E09F2E] ring-offset-2 motion-safe:animate-tutorial-ring";

const GAP = 14; // odstep dymka od podswietlonego elementu
const CARET = 9; // polowa przekatnej dziobka
const CARET_INSET = 24; // dziobek nie moze wejsc na zaokraglony rog karty
const EST_HEIGHT = 150; // zapas wysokosci przy decyzji "gora czy dol"
const CARD_WIDTH = 368; // 23rem — fallback zanim zmierzymy realna karte

// Zmiana kroku: stary dymek gasnie, strona sie uklada (scroll, rozwiniecie karty),
// dopiero potem nowy dymek wjezdza JUZ na swoim miejscu. Wczesniej pozycja zmieniala
// sie w trakcie animacji i dymek przeskakiwal.
const EXIT_MS = 160;
const FIRST_SHOW_DELAY_MS = 300; // wejscie na ekran samouczka — niech strona najpierw stanie
const SETTLE_MAX_MS = 1000;
// Minimalna pauza miedzy krokami: dymek nie moze wyskoczyc w tej samej chwili co
// klikniecie — oko musi najpierw zobaczyc reakcje ekranu (podswietlenie, rozwiniecie).
const MIN_STEP_GAP_MS = 380;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

// Czeka, az cel przestanie sie ruszac (smooth scroll, rozwijanie karty wzorow).
const waitForStable = async (el, isCancelled) => {
    if (!el) {
        await nextFrame();
        return;
    }
    const start = performance.now();
    let last = null;
    let stableFrames = 0;
    while (!isCancelled() && performance.now() - start < SETTLE_MAX_MS) {
        await nextFrame();
        const r = el.getBoundingClientRect();
        const same =
            last &&
            Math.abs(r.top - last.top) < 0.5 &&
            Math.abs(r.left - last.left) < 0.5 &&
            Math.abs(r.width - last.width) < 0.5 &&
            Math.abs(r.height - last.height) < 0.5;
        stableFrames = same ? stableFrames + 1 : 0;
        if (stableFrames >= 4) return;
        last = r;
    }
};

const readSafeZone = () => {
    const header = document.querySelector("header");
    const nav = document.querySelector("[data-bottom-nav]");
    const headerRect = header?.getBoundingClientRect();
    const navRect = nav && nav.offsetParent !== null ? nav.getBoundingClientRect() : null;
    return {
        top: headerRect?.height ? headerRect.bottom + 8 : 80,
        bottom: navRect?.height ? window.innerHeight - navRect.top + 8 : 88,
    };
};

const TutorialCoachmark = ({
    targetSelector,
    title,
    body,
    cta,
    onCta,
    onSkip,
    stepIndex,
    stepCount,
    fallbackBottom,
    // "screen-bottom" — nie kotwicz sie przy celu, zejdz na dol ekranu. Dla
    // krokow, w ktorych zakotwiczony dymek przykrylby to, co user wlasnie
    // odslonil (rozwinieta karta wzorow, tresc zadania). Cel i tak swieci.
    preferredPlace,
    // Brudnopis to portal na z-[99999] — dymek musi umiec wejsc nad niego.
    zClass = "z-30",
}) => {
    const prefersReducedMotion = useReducedMotion();
    const cardRef = useRef(null);
    const wrapperRef = useRef(null);
    // `view` to migawka tego, co dymek pokazuje (tresc + pozycja). Podmieniamy ja
    // wylacznie, gdy dymek jest niewidoczny — nic nie skacze na oczach usera.
    const [view, setView] = useState(null);
    const [shown, setShown] = useState(false);
    const shownRef = useRef(false);
    shownRef.current = shown;
    // Strona (nad/pod) ustalona raz na krok. Przy przewijaniu dymek tylko jedzie
    // za celem, nie przerzuca sie na druga strone.
    const sideRef = useRef(null);
    const isFirstShowRef = useRef(true);
    const contentRef = useRef(null);
    contentRef.current = { title, body, cta, stepIndex };

    const computePos = useCallback((lockedBelow) => {
        // Plywajacy przycisk (FAB brudnopisu) ma wyzszy z-index niz dymek, wiec
        // gdy pasy pionowe sie przecinaja, oddajemy mu jego kolumne (zwezenie
        // od prawej). Test jest wylacznie pionowy, wiec zwezenie nie moze
        // uniewaznic wlasnego warunku (petla pomiarow).
        const avoid = document.querySelector("[data-coach-avoid]");
        const avoidRect = avoid?.getBoundingClientRect();
        const clashes = (top, bottom) =>
            avoidRect?.width && avoidRect.bottom > top && avoidRect.top < bottom
                ? Math.round(window.innerWidth - avoidRect.left + 8)
                : 0;

        const el =
            targetSelector && preferredPlace !== "screen-bottom"
                ? document.querySelector(targetSelector)
                : null;
        const rect = el?.getBoundingClientRect();
        if (!rect || (!rect.width && !rect.height)) {
            // Dol ekranu: pas zajmowany przez dymek bierzemy z realnej karty.
            const card = cardRef.current?.getBoundingClientRect();
            return {
                anchored: false,
                padRight: card?.height ? clashes(card.top, card.bottom) : 0,
            };
        }
        const safe = readSafeZone();
        const spaceBelow = window.innerHeight - safe.bottom - rect.bottom;
        const spaceAbove = rect.top - safe.top;
        const below =
            preferredPlace === "below"
                ? true
                : preferredPlace === "above"
                ? false
                : typeof lockedBelow === "boolean"
                ? lockedBelow
                : spaceBelow >= EST_HEIGHT + GAP || spaceBelow > spaceAbove;

        // Pas zajmowany przez dymek szacujemy z EST_HEIGHT (karta moze jeszcze
        // stac w starym miejscu, wiec jej wymiar nie nadaje sie na decyzje).
        const bandTop = below ? rect.bottom + GAP : rect.top - GAP - EST_HEIGHT;
        const bandBottom = below ? rect.bottom + GAP + EST_HEIGHT : rect.top - GAP;
        const padRight = clashes(bandTop, bandBottom);

        // POZIOM: karta jedzie za celem, nie stoi na srodku okna. Na telefonie
        // roznicy nie widac (wszystko jest przy srodku), ale na tablecie cel
        // potrafi byc 300 px w bok i wysrodkowany dymek wygladal, jakby wskazywal
        // co innego. Pas liczymy Z POMINIECIEM kolumny FAB-a: getBoundingClientRect
        // zwraca box razem z paddingiem, wiec bez odjecia `padRight` karta mogla by
        // wjechac pod przycisk. Offset nie zmienia szerokosci karty, wiec pomiar
        // sie nie zapetla.
        const wrapper = wrapperRef.current?.getBoundingClientRect();
        const card = cardRef.current?.getBoundingClientRect();
        const wrapLeft = wrapper?.width ? wrapper.left : 16;
        const wrapWidth = (wrapper?.width || window.innerWidth - 32) - padRight;
        const cardWidth = Math.min(card?.width || CARD_WIDTH, wrapWidth);
        const targetCenter = rect.left + rect.width / 2;
        const offsetLeft = Math.min(
            Math.max(targetCenter - cardWidth / 2 - wrapLeft, 0),
            Math.max(0, wrapWidth - cardWidth)
        );

        // Dziobek celuje w srodek elementu, ale nie wychodzi poza rogi karty.
        const caretLeft = Math.min(
            Math.max(targetCenter - (wrapLeft + offsetLeft), CARET_INSET),
            Math.max(CARET_INSET, cardWidth - CARET_INSET)
        );

        return {
            anchored: true,
            below,
            top: below ? Math.round(rect.bottom + GAP) : undefined,
            bottom: below ? undefined : Math.round(window.innerHeight - rect.top + GAP),
            caretLeft: Math.round(caretLeft),
            offsetLeft: Math.round(offsetLeft),
            padRight,
        };
    }, [targetSelector, preferredPlace]);

    // Element moze byc poza ekranem (dluga tresc zadania) — najpierw go dowozimy,
    // potem mierzymy. Elementow `fixed` (FAB) nie scrollujemy, bo i tak sa widoczne.
    // Przy wymuszonej stronie doscrollowujemy tez wtedy, gdy cel jest widoczny,
    // ale nie ma za nim miejsca na dymek — inaczej dymek wladowalby sie na tresc.
    const scrollTargetIntoView = useCallback((el) => {
        if (!el || getComputedStyle(el).position === "fixed") return;
        const rect = el.getBoundingClientRect();
        const safe = readSafeZone();
        const offscreen =
            rect.top < safe.top || rect.bottom > window.innerHeight - safe.bottom;
        const cramped =
            preferredPlace === "below"
                ? window.innerHeight - safe.bottom - rect.bottom < EST_HEIGHT + GAP
                : preferredPlace === "above"
                ? rect.top - safe.top < EST_HEIGHT + GAP
                : false;
        if (offscreen || cramped) {
            el.scrollIntoView({
                block: "center",
                behavior: prefersReducedMotion ? "auto" : "smooth",
            });
        }
    }, [preferredPlace, prefersReducedMotion]);

    // Sekwencja kroku: zgas stary dymek -> dowiez cel -> poczekaj, az strona stanie ->
    // ustaw tresc i pozycje (niewidocznie) -> domierz realna karte -> pokaz.
    useEffect(() => {
        let cancelled = false;
        const isCancelled = () => cancelled;
        (async () => {
            const startedAt = performance.now();
            setShown(false);
            sideRef.current = null;
            await wait(isFirstShowRef.current ? FIRST_SHOW_DELAY_MS : EXIT_MS);
            isFirstShowRef.current = false;
            if (cancelled) return;

            const el = targetSelector ? document.querySelector(targetSelector) : null;
            scrollTargetIntoView(el);
            await waitForStable(el, isCancelled);
            if (cancelled) return;

            const first = computePos(null);
            sideRef.current = first.anchored ? first.below : null;
            setView({ ...contentRef.current, pos: first });

            // Karta stoi juz z nowa trescia w nowym miejscu — dopiero teraz jej wymiar
            // (dziobek, kolizja z FAB-em) jest prawdziwy.
            await nextFrame();
            await nextFrame();
            if (cancelled) return;
            setView((v) => v && { ...v, pos: computePos(sideRef.current) });
            await wait(Math.max(0, MIN_STEP_GAP_MS - (performance.now() - startedAt)));
            if (!cancelled) setShown(true);
        })();
        return () => {
            cancelled = true;
        };
    }, [stepIndex, targetSelector, computePos, scrollTargetIntoView]);

    // Widoczny dymek jedzie za celem przy przewijaniu / zmianie rozmiaru — bez
    // zmiany strony, wiec bez przeskokow.
    useEffect(() => {
        let ticking = false;
        const follow = () => {
            if (ticking || !shownRef.current) return;
            ticking = true;
            requestAnimationFrame(() => {
                ticking = false;
                if (!shownRef.current) return;
                setView((v) => v && { ...v, pos: computePos(sideRef.current) });
            });
        };
        window.addEventListener("resize", follow);
        // Tresc scrolluje sie w <main>, wiec zdarzenie nie dolatuje do window
        // bablowaniem — lapiemy je w fazie przechwytywania.
        window.addEventListener("scroll", follow, true);
        return () => {
            window.removeEventListener("resize", follow);
            window.removeEventListener("scroll", follow, true);
        };
    }, [computePos]);

    const pos = view?.pos;
    const anchored = !!pos?.anchored;
    const hiddenY = prefersReducedMotion ? 0 : anchored && !pos.below ? 6 : -6;
    const content = view || contentRef.current;

    return (
        <div
            ref={wrapperRef}
            className={`fixed left-4 right-4 pointer-events-none ${zClass}`}
            style={
                anchored
                    ? {
                          ...(pos.below ? { top: pos.top } : { bottom: pos.bottom }),
                          paddingRight: pos.padRight || undefined,
                      }
                    : { bottom: fallbackBottom, paddingRight: pos?.padRight || undefined }
            }
        >
            <div
                className="relative w-[23rem] max-w-full"
                style={{
                    marginLeft: anchored
                        ? pos.offsetLeft
                        : "auto",
                    marginRight: anchored ? 0 : "auto",
                }}
                aria-live="polite"
                aria-atomic="true"
            >
                    <motion.div
                        ref={cardRef}
                        initial={false}
                        animate={
                            shown
                                ? { opacity: 1, y: 0, scale: 1 }
                                : { opacity: 0, y: hiddenY, scale: prefersReducedMotion ? 1 : 0.985 }
                        }
                        transition={
                            shown
                                ? { duration: prefersReducedMotion ? 0.15 : 0.3, ease: [0.22, 1, 0.36, 1] }
                                : { duration: prefersReducedMotion ? 0.1 : EXIT_MS / 1000, ease: "easeIn" }
                        }
                        className={`relative bg-[#233855] text-white rounded-2xl px-4 py-3.5 shadow-[0_10px_30px_rgba(35,56,85,0.32)] ${
                            shown ? "pointer-events-auto" : "pointer-events-none"
                        }`}
                    >
                        {anchored && (
                            <span
                                aria-hidden="true"
                                className="absolute w-[18px] h-[18px] bg-[#233855] rotate-45 rounded-[3px]"
                                style={{
                                    left: pos.caretLeft - CARET,
                                    [pos.below ? "top" : "bottom"]: -CARET,
                                }}
                            />
                        )}

                        {/* "Pomin" nie wychodzi od razu — pyta o potwierdzenie, bo samouczka
                            nie da sie potem odzyskac. Bez tej bramki bylby to odruchowy
                            "zamknij to" na pierwszym ekranie. */}
                        <div className="relative flex items-center justify-between gap-3 mb-2">
                            <div className="flex items-center gap-1.5" aria-hidden="true">
                                {Array.from({ length: stepCount }).map((_, i) => (
                                    <span
                                        key={i}
                                        className={`h-1.5 rounded-full transition-all duration-300 ${
                                            i === content.stepIndex
                                                ? "w-4 bg-[#E09F2E]"
                                                : i < content.stepIndex
                                                ? "w-1.5 bg-[#E09F2E]/60"
                                                : "w-1.5 bg-white/25"
                                        }`}
                                    />
                                ))}
                            </div>
                            <span className="sr-only">
                                Krok {content.stepIndex + 1} z {stepCount}
                            </span>
                            {onSkip && (
                                <button
                                    onClick={onSkip}
                                    className="-my-3 -mr-2 px-2 min-h-[44px] flex items-center text-xs text-white/75 hover:text-white transition-colors"
                                >
                                    Pomiń
                                </button>
                            )}
                        </div>

                        {/* Awatarka nadawcy: samouczek mowi w pierwszej osobie, wiec to "ja"
                            musi miec twarz — ta sama, ktora user spotka pozniej w czacie AI.
                            Siedzi w linii tytulu (20 px = wysokosc wiersza), zeby nie dolozyc
                            dymkowi ani jednego piksela wysokosci. */}
                        <div className="relative flex items-center gap-2 mb-1.5">
                            <span className="w-5 h-5 rounded-full bg-white/10 overflow-hidden flex-shrink-0">
                                <img
                                    src={orzelLogo}
                                    alt=""
                                    aria-hidden="true"
                                    className="w-full h-full object-cover"
                                />
                            </span>
                            <h2 className="font-apotek uppercase tracking-widest text-sm font-bold text-[#E09F2E]">
                                {content.title}
                            </h2>
                        </div>
                        <p className="relative text-sm text-white/85 leading-relaxed">{content.body}</p>

                        {/* Zadnego wiersza "kliknij X": osobny blok konkurowal o uwage
                            z podswietlonym elementem i podnosil dymek o kolejne 40 px.
                            Czynnosc siedzi w ostatnim zdaniu `body`. */}
                        {content.cta && (
                            <button
                                onClick={onCta}
                                className="relative mt-3 w-full min-h-[44px] bg-[#E09F2E] text-[#233855] font-bold rounded-lg text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                            >
                                {content.cta}
                                <FiArrowRight size={16} />
                            </button>
                        )}
                    </motion.div>
            </div>
        </div>
    );
};

export default TutorialCoachmark;
